import * as Sentry from '@sentry/nextjs'
import { nanoid } from 'nanoid'
import Router from 'next/router'

import { gqlRequestClient } from '@/api/clients'

import { GraphQLResponseError, QueueItem } from './types'

let isProcessRedirection = false
let failedQueue: QueueItem[] = []

const processRedirection = async (): Promise<void> => {
  try {
    if (isProcessRedirection) {
      return
    }
    isProcessRedirection = true

    let destination = `/?redirect_url=${encodeURIComponent(Router.asPath)}&logout=true`
    if (Router.pathname === '/') {
      destination = `/?logout=true`
    }

    Router.push(destination)
  } catch (error) {
    return Promise.reject(error)
  } finally {
    isProcessRedirection = false
    failedQueue = []
  }
}

export function fetcher<TData, TVariables extends { [key: string]: any }>(
  query: string,
  variables?: TVariables,
  options?: { signal: AbortSignal },
) {
  return async (): Promise<TData> => {
    //set request id
    const requestId = nanoid(36)
    Sentry.getCurrentScope().setTag('request_id', requestId)
    gqlRequestClient.setHeader('x-request-id', requestId)

    try {
      return await gqlRequestClient.request({
        document: query,
        variables,
      })
    } catch (error) {
      const typedError = error as GraphQLResponseError

      if (
        typedError.response?.errors?.some(
          (e) => e.extensions?.originalError?.statusCode === 401,
        )
      ) {
        if (!isProcessRedirection) {
          await processRedirection()
          return await gqlRequestClient.request({
            document: query,
            variables,
            signal: options?.signal,
          })
        } else {
          // if the token is being refreshed, we add the failed request to the queue
          return new Promise<TData>((resolve, reject) => {
            failedQueue.push({
              query,
              variables,
              resolve,
              reject,
            })
          })
        }
      } else {
        if (!(error instanceof Error && error.name === 'AbortError')) {
          Sentry.captureException(error)
        }
        throw typedError
      }
    }
  }
}
